import type { MetaFunction } from 'react-router'
import { useLoaderData, Link } from 'react-router'
import { db } from '@afficheservices/db'
import { geoFrance, professionalProfiles } from '@afficheservices/db/schema'
import { sql } from 'drizzle-orm'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { BreadcrumbSchema } from '~/components/structured-data'
import { STATIC_PAGE_HEADERS } from '~/lib/cache.server'
import { MapPin, ArrowRight, Building2 } from 'lucide-react'
import { VoirAussi, CROSS_LINKS } from '~/components/voir-aussi'

export function headers() { return STATIC_PAGE_HEADERS }

export const meta: MetaFunction = () => [
  { title: "Artisans par département — 101 départements | AfficheServices" },
  { name: 'description', content: "Trouvez un artisan dans votre département. 101 départements français couverts avec des artisans vérifiés SIRET." },
  { tagName: 'link', rel: 'canonical', href: 'https://www.afficheservices.fr/departements' },
  { property: 'og:title', content: 'Artisans par département — 101 départements | AfficheServices' },
  { property: 'og:description', content: 'Trouvez un artisan dans votre département. 101 départements français couverts.' },
  { property: 'og:url', content: 'https://www.afficheservices.fr/departements' },
]

export async function loader() {
  // Get all distinct departments from geoFrance with city counts
  const depts = await db
    .select({
      code: geoFrance.departmentCode,
      name: geoFrance.departmentName,
      regionName: geoFrance.regionName,
      cityCount: sql<number>`count(DISTINCT ${geoFrance.communeName})::int`,
    })
    .from(geoFrance)
    .where(sql`${geoFrance.departmentCode} NOT LIKE '97%' AND ${geoFrance.departmentCode} NOT LIKE '98%'`)
    .groupBy(geoFrance.departmentCode, geoFrance.departmentName, geoFrance.regionName)
    .orderBy(geoFrance.departmentCode)

  // Pro counts per department
  const proCounts = await db
    .select({
      dept: professionalProfiles.departmentCode,
      count: sql<number>`count(*)::int`,
    })
    .from(professionalProfiles)
    .groupBy(professionalProfiles.departmentCode)

  const proCountMap = new Map<string, number>()
  for (const r of proCounts) proCountMap.set(r.dept, Number(r.count))

  const departments = depts.map((d) => ({
    code: d.code,
    name: d.name,
    regionName: d.regionName,
    cityCount: Number(d.cityCount),
    proCount: proCountMap.get(d.code) ?? 0,
  }))

  // Group by region for display
  const byRegion = new Map<string, typeof departments>()
  for (const d of departments) {
    const list = byRegion.get(d.regionName) ?? []
    list.push(d)
    byRegion.set(d.regionName, list)
  }

  const totalDepts = departments.length
  const totalPros = departments.reduce((s, d) => s + d.proCount, 0)

  return {
    byRegion: [...byRegion.entries()].sort(([a], [b]) => a.localeCompare(b, 'fr')),
    totalDepts,
    totalPros,
  }
}

export default function DepartementsIndexPage() {
  const { byRegion, totalDepts, totalPros } = useLoaderData<typeof loader>()

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Départements', url: 'https://www.afficheservices.fr/departements' },
      ]} />

      <section className="bg-gradient-to-br from-blue-900 via-indigo-900 to-slate-900 px-4 py-14 text-white sm:py-20">
        <div className="mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-medium text-blue-200">
            <Building2 className="h-3.5 w-3.5" /> Couverture nationale
          </div>
          <h1 className="mt-5 text-3xl font-extrabold sm:text-5xl">
            Artisans par département
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-blue-200">
            {totalDepts} départements · {totalPros.toLocaleString('fr-FR')}+ artisans vérifiés.
            Trouvez un professionnel de confiance près de chez vous.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 lg:px-6">
        {byRegion.map(([regionName, depts]) => (
          <section key={regionName} className="mt-10 first:mt-0">
            <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900">
              <MapPin className="h-4 w-4 text-primary" /> {regionName}
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">
                {depts.length} dép.
              </span>
            </h2>
            <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {depts.map((d) => (
                <Link
                  key={d.code}
                  to={`/departement/${d.code}`}
                  className="group flex items-center justify-between rounded-xl border border-border bg-white p-4 shadow-xs transition-all hover:border-primary hover:shadow-md"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 group-hover:text-primary">
                      {d.name} ({d.code})
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {d.proCount > 0 ? `${d.proCount.toLocaleString('fr-FR')} artisans` : 'Bientôt'}
                      {' · '}{d.cityCount} villes
                    </p>
                  </div>
                  <ArrowRight className="h-4 w-4 shrink-0 text-slate-300 group-hover:text-primary" />
                </Link>
              ))}
            </div>
          </section>
        ))}

        <VoirAussi links={CROSS_LINKS.geo} />

        <section className="mt-16 text-center">
          <h2 className="text-2xl font-bold text-slate-900">Besoin d'un artisan ?</h2>
          <p className="mt-2 text-sm text-slate-500">
            Décrivez votre projet et recevez 3 devis gratuits sous 48h.
          </p>
          <Link
            to="/devis-gratuit"
            className="mt-5 inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-bold text-white hover:bg-primary-hover"
          >
            Demander mes devis <ArrowRight className="h-4 w-4" />
          </Link>
        </section>
      </div>

      <Footer />
    </div>
  )
}
